"use client";

import * as React from "react";
import { MoreHorizontal } from "@/components/icons";

import { cn } from "@/lib/utils";
import { IconButton } from "@/components/ui/icon-button";
import { GroupedList, GroupedListSection } from "@/components/ui/grouped-list";
import { SectionSwitcherSheet, useSectionSwitcherClose } from "@/components/ui/section-switcher";

export function PhoneActionBar({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="phone-action-bar"
      className={cn(
        "sticky bottom-0 z-10 -mx-3 -mb-3 mt-4 flex flex-wrap items-center gap-3 border-t border-border bg-background px-4 py-3 md:hidden [&>*:not([data-slot=phone-action-more]):not(.basis-full)]:min-h-11 [&>*:not([data-slot=phone-action-more]):not(.basis-full)]:flex-1",
        className,
      )}
      {...props}
    />
  );
}

export function PhoneActionSheet({
  title = "Actions",
  className,
  children,
}: {
  title?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      data-slot="phone-action-more"
      className={cn("shrink-0 md:hidden", className)}
    >
      <IconButton
        size="default"
        aria-label={title}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <MoreHorizontal aria-hidden="true" />
      </IconButton>
      <SectionSwitcherSheet open={open} onOpenChange={setOpen} title={title}>
        <GroupedList>
          <GroupedListSection>{children}</GroupedListSection>
        </GroupedList>
      </SectionSwitcherSheet>
    </div>
  );
}

export function PhoneActionItem(props: React.ComponentProps<"li">) {
  return <li {...props} />;
}

export function PhoneActionRowButton({
  className,
  tone = "default",
  closesSheet = false,
  onClick,
  ...props
}: React.ComponentProps<"button"> & { tone?: "default" | "destructive"; closesSheet?: boolean }) {
  const closeSheet = useSectionSwitcherClose();
  return (
    <button
      type="button"
      onClick={(event) => {
        onClick?.(event);
        if (closesSheet) closeSheet();
      }}
      className={cn(
        "flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left text-base transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none active:bg-surface-3",
        tone === "destructive" ? "text-destructive-strong" : "text-foreground",
        className,
      )}
      {...props}
    />
  );
}
